// hooks/useLeads.ts

import { useQuery, useMutation, useQueryClient, UseQueryResult } from "@tanstack/react-query";
import { Lead, LeadStatus } from "../../interfaces/DTO/crm/lead";
import { LeadPortalLinkResponse } from "../../interfaces/DTO/public/leadPortal";
import { leadService } from "../../services/crm/leadService";
import { createCachedEntityHooks } from "../createEntityHooks";

export const {
  useAll: useLeads,
  useEntityById: useLeadById,
  useCreateEntity: useCreateLead,
  useUpdateEntity: useUpdateLead,
  usePartialUpdateEntity: usePartialUpdateLead,
  useDeleteEntity: useDeleteLead,

} = createCachedEntityHooks<Lead>({
  entityName: "leads",
  service: leadService,
  enableCacheById: true,
  staleTimeMs: 5000,
});

/**
 * Hook לשיוך קמפיין לרשימת לידים
 */
export const useLeadConsentFiles = (leadId: number | undefined) =>
  useQuery({
    queryKey: ["leads", leadId, "consent-files"],
    queryFn: () => leadService.getConsentFiles(leadId!),
    enabled: !!leadId,
    staleTime: 30000,
  });

export const useCreateDealFromLead = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (leadId: number) => leadService.createDeal(leadId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["leads"] });
      queryClient.invalidateQueries({ queryKey: ["deals"] });
    },
  });
};

export const useFixCopyLeadFiles = () => {
  return useMutation({
    mutationFn: (leadId: number) => leadService.fixCopyFiles(leadId),
  });
};

export const useBulkAssignCampaign = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ leadIds, campaignId }: { leadIds: number[]; campaignId: number | null }) =>
      leadService.bulkAssignCampaign(leadIds, campaignId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["leads"] }),
  });
};

/**
 * Hook לשליפת לידים לפי סטטוס
 */
export const useLeadsByStatus = (
  status: LeadStatus,
  enabled: boolean = true
): UseQueryResult<Lead[], Error> => {
  return useQuery({
    queryKey: ["leads", "by-status", status],
    queryFn: () => leadService.getByStatus(status),
    enabled,
    staleTime: 5000,
  });
};

/**
 * Hook לשליפת לידים לפי סוכן משוייך
 */
export const useLeadsByAgent = (
  agentId: number,
  enabled: boolean = true
): UseQueryResult<Lead[], Error> => {
  return useQuery({
    queryKey: ["leads", "by-agent", agentId],
    queryFn: () => leadService.getByAgent(agentId),
    enabled,
    staleTime: 5000,
  });
};

/**
 * Hook לשליפת לידים לפי קמפיין
 */
export const useLeadsByCampaign = (
  campaignId: number,
  enabled: boolean = true
): UseQueryResult<Lead[], Error> => {
  return useQuery({
    queryKey: ["leads", "by-campaign", campaignId],
    queryFn: () => leadService.getByCampaign(campaignId),
    enabled,
    staleTime: 5000,
  });
};

/**
 * Hook לחיפוש ליד לפי מספר טלפון
 */
export const useLeadByPhone = (
  phone: string,
  enabled: boolean = true
): UseQueryResult<Lead | null, Error> => {
  return useQuery({
    queryKey: ["leads", "by-phone", phone],
    queryFn: () => leadService.getByPhone(phone),
    enabled: enabled && !!phone,
    staleTime: 5000,
  });
};

/**
 * Hook לחיפוש ליד לפי כתובת אימייל
 */
export const useLeadByEmail = (
  email: string,
  enabled: boolean = true
): UseQueryResult<Lead | null, Error> => {
  return useQuery({
    queryKey: ["leads", "by-email", email],
    queryFn: () => leadService.getByEmail(email),
    enabled: enabled && !!email,
    staleTime: 5000,
  });
};

/**
 * Hook להתחלת תהליך - הלקוח רוצה להתקדם
 */
export const useStartLeadFlow = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (leadId: number) => leadService.startFlow(leadId),
    onSuccess: (updatedLead) => {
      queryClient.invalidateQueries({ queryKey: ["leads"] });
      if (updatedLead.id) {
        queryClient.setQueryData(["leads", updatedLead.id], updatedLead);
      }
    },
  });
};

/**
 * Hook לסימון ליד כ"בירר בלבד"
 */
export const useMarkLeadAsInquired = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ leadId, notes }: { leadId: number; notes?: string }) =>
      leadService.markAsInquired(leadId, notes),
    onSuccess: (updatedLead) => {
      queryClient.invalidateQueries({ queryKey: ["leads"] });
      if (updatedLead.id) {
        queryClient.setQueryData(["leads", updatedLead.id], updatedLead);
      }
    },
  });
};

/**
 * Hook ליצירת לינק פורטל ללקוח
 */
export const useCreateLeadPortalLink = () =>
  useMutation<LeadPortalLinkResponse, Error, number>({
    mutationFn: (leadId) => leadService.createPortalLink(leadId),
  });

export const useGetLeadPortalLink = () =>
  useMutation<LeadPortalLinkResponse | null, Error, number>({
    mutationFn: (leadId) => leadService.getPortalLink(leadId),
  });

/**
 * Hook לבחירת חבילה - מסך 4/5
 * ממיר Lead → Contact, יוצר Party, PartyParticipant, ו-Deal
 */
export const useSelectPackage = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      leadId,
      pricingPlanId,
      partnerDetails,
    }: {
      leadId: number;
      pricingPlanId: number;
      partnerDetails?: {
        first_name: string;
        last_name: string;
        phone?: string;
        email?: string;
      };
    }) => leadService.selectPackage(leadId, pricingPlanId, partnerDetails),
    onSuccess: () => {
      // Invalidate all related queries
      queryClient.invalidateQueries({ queryKey: ["leads"] });
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
      queryClient.invalidateQueries({ queryKey: ["parties"] });
      queryClient.invalidateQueries({ queryKey: ["deals"] });
    },
  });
};
